// GSAP FLIP for sortable `<.table>` — but driven by the client, not the
// server. The hook intercepts header clicks, reorders the tbody's `<tr>`s
// in-place, plays the FLIP tween, AND lets the existing `phx-click` push
// the same event to the server. The server's eventual response sorts the
// same rows the same way → morphdom keys the `<tr>`s by `id` and sees no
// change → no flicker, no race.
//
// The hook is attached to the `<tbody>` inside `<.table>`. The tbody's id
// MUST be stable across renders — see `lib/operation_web/components/ui/table.ex`
// — and so must each `<tr id>`. Without those, morphdom would replace
// nodes wholesale on every patch and the animation never runs.
//
// `beforeUpdate` / `updated` are retained for server-initiated reorderings
// (initial load, broadcast updates, etc.) — they're no-ops when the
// optimistic client sort has already put rows in the right place.

import gsap from "gsap";
import type { LiveViewHook } from "./types";
// `SortDir` is dox-owned in `domain/ui/chrome.dox` — single source of
// truth shared with the Elixir producer in
// `lib/operation_web/components/ui/table.ex` (which encodes it through
// `Dox.Ui.SortDir.to_json/1` into the tbody `data-sort-state` attr).
// Importing the type here means a rename in dox lands as a TS error on
// the `dir !== "asc" && dir !== "desc"` guard, not a silent listener
// that falls through to the inactive class branch.
import { type SortDir, validSortDir } from "../../../.dox/ui";

interface SortState {
  key: string;
  dir: SortDir;
}

// State stashed on `this` between hook lifecycle callbacks.
export interface TableSortAnimateState {
  _prevRects: Map<string, DOMRect> | undefined;
  _table: HTMLTableElement | null;
  _clickHandler: ((e: Event) => void) | null;
}

// Pure: walk the rows under `el` and capture their bounding rects keyed
// by `data-row-id`. Rows without an id are skipped (rare; only happens
// for slot-driven tables, which don't opt into sort animations anyway).
const snapshotRowRects = (el: HTMLElement): Map<string, DOMRect> => {
  const map = new Map<string, DOMRect>();
  const rows = el.querySelectorAll<HTMLElement>("tr[data-row-id]");
  for (const row of rows) {
    const id = row.getAttribute("data-row-id");
    if (id !== null && id !== "") {
      map.set(id, row.getBoundingClientRect());
    }
  }
  return map;
};

// Pure-ish: for each row that existed before AND after the reorder, compute
// its dy and start a GSAP tween from the old offset back to 0. Rows that
// didn't move (dy === 0) are skipped — re-clicking the same sort key
// shouldn't trigger a 350ms animation. `overwrite: "auto"` kills any
// in-flight tween on the same target so rapid re-clicks don't fight.
const playFlip = (el: HTMLElement, prev: Map<string, DOMRect>): void => {
  const rows = el.querySelectorAll<HTMLElement>("tr[data-row-id]");
  for (const row of rows) {
    const id = row.getAttribute("data-row-id");
    if (id === null || id === "") continue;
    const prevRect = prev.get(id);
    if (prevRect === undefined) continue;
    const currentRect = row.getBoundingClientRect();
    const dy = prevRect.top - currentRect.top;
    if (dy === 0) continue;
    gsap.fromTo(
      row,
      { y: dy },
      { y: 0, duration: 0.35, ease: "power2.out", overwrite: "auto" },
    );
  }
};

// Parse `"<key>:<dir>"` from a `data-sort-state` / `data-next-sort-state`
// attribute. Returns null when absent, empty, or malformed. The `<dir>`
// half is blessed through `validSortDir` (dox-emitted) so the closed
// `"asc" | "desc"` set lives in one place (`domain/ui/chrome.dox`); a
// future third direction would land as a TS exhaustiveness error at the
// `state.dir === "desc"` switches below, not as a silent fall-through.
// `validSortDir` THROWS on an unknown tag — but this parser is at the
// DOM-attr boundary where malformed input shouldn't crash the page, so
// the throw is caught and remapped to `null` (preserves the legacy
// graceful-degradation contract callers depend on).
const parseSortStateAttr = (raw: string | null): SortState | null => {
  if (raw === null || raw === "") return null;
  const idx = raw.indexOf(":");
  if (idx < 1) return null;
  const key = raw.slice(0, idx);
  const dirRaw = raw.slice(idx + 1);
  try {
    const dir: SortDir = validSortDir(dirRaw);
    return { key, dir };
  } catch {
    return null;
  }
};

// Narrow `unknown` to the leaf JSON types we actually emit from
// `encode_sort_value/1` in table.ex — strings, numbers, booleans, null.
// Anything else (objects, arrays) means upstream emitted something the
// comparator can't reason about; treat it as null so the row sinks to
// the top in asc / bottom in desc rather than fall through to a
// "[object Object]" stringification that violates lint AND would sort
// every "complex" cell identically.
const coerceComparable = (v: unknown): string | number | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === "number" || typeof v === "string") return v;
  if (typeof v === "boolean") return v ? "true" : "false";
  return null;
};

// Compare two row sort values, matching Elixir's Erlang term order well
// enough for the values we actually emit (see `encode_sort_value/1` in
// table.ex). Numbers compare numerically; everything else stringifies
// and compares lexicographically. `null` sorts first (ascending) — same
// as nil in Erlang term order against strings/numbers.
const compareValues = (a: unknown, b: unknown): number => {
  const ca = coerceComparable(a);
  const cb = coerceComparable(b);
  if (ca === null) return cb === null ? 0 : -1;
  if (cb === null) return 1;
  if (typeof ca === "number" && typeof cb === "number") return ca - cb;
  const sa = typeof ca === "number" ? ca.toString() : ca;
  const sb = typeof cb === "number" ? cb.toString() : cb;
  return sa < sb ? -1 : sa > sb ? 1 : 0;
};

// Read the requested key out of a row's `data-sort-values` JSON blob.
// Missing key / malformed JSON → null (sorts first in asc, last in desc).
const rowSortValue = (row: HTMLElement, key: string): unknown => {
  const blob = row.getAttribute("data-sort-values");
  if (blob === null || blob === "") return null;
  try {
    const obj = JSON.parse(blob) as Record<string, unknown>;
    const v = obj[key];
    return v === undefined ? null : v;
  } catch {
    return null;
  }
};

// In-place reorder of `<tr>` children under `tbody` to match `state`.
// `Array.prototype.sort` is stable (TC39 since 2019), so ties preserve
// their previous relative order — same shape as Elixir's `Enum.sort_by`.
// Appending an already-attached child *moves* it (DOM spec), so the
// re-append loop is a cheap O(n) reorder, no detach-reattach cycle.
const sortRowsInDom = (tbody: HTMLElement, state: SortState): void => {
  const rows = Array.from(
    tbody.querySelectorAll<HTMLTableRowElement>("tr[data-sort-values]"),
  );
  const dirMul = state.dir === "desc" ? -1 : 1;
  rows.sort(
    (a, b) =>
      dirMul *
      compareValues(rowSortValue(a, state.key), rowSortValue(b, state.key)),
  );
  for (const row of rows) tbody.appendChild(row);
};

// Optimistically mirror the new sort state on header chrome so the user
// doesn't watch rows reorder for 50ms before the chevron catches up:
//   * tbody `data-sort-state` → "<key>:<dir>"
//   * <th> `aria-sort` → "ascending" | "descending" | "none"
//   * sort indicator span → swaps `class` between the three pre-rendered
//     variants the server stamped on the span as
//     `data-class-active-asc`, `data-class-active-desc`,
//     `data-class-inactive`. The TS hook does NOT know the specific
//     Tailwind classes — the server is the single source of truth. A
//     change to `sort_indicator/1` (table.ex) automatically reaches the
//     client via the data attrs; no second source to keep in sync.
// When the server response lands the SAME state, morphdom sees no diff
// and nothing flickers.
const applyHeaderState = (
  tbody: HTMLElement,
  table: HTMLTableElement,
  state: SortState,
): void => {
  tbody.setAttribute("data-sort-state", `${state.key}:${state.dir}`);

  const buttons = table.querySelectorAll<HTMLButtonElement>(
    "button[data-sort-key]",
  );
  for (const btn of buttons) {
    const key = btn.getAttribute("data-sort-key");
    const th = btn.closest<HTMLTableCellElement>("th");
    const indicator = btn.querySelector<HTMLElement>("span");
    const active = key === state.key;

    if (th !== null) {
      th.setAttribute(
        "aria-sort",
        active ? (state.dir === "asc" ? "ascending" : "descending") : "none",
      );
    }
    if (indicator !== null) {
      const cls = active
        ? state.dir === "asc"
          ? indicator.getAttribute("data-class-active-asc")
          : indicator.getAttribute("data-class-active-desc")
        : indicator.getAttribute("data-class-inactive");
      if (cls !== null && cls !== "") indicator.className = cls;
    }
  }
};

// Sortable-header click handler: snapshot, reorder, mirror chrome, FLIP.
// Lets the click bubble so the colocated `phx-click` still pushes the
// `sort_*` event to the server — keeping LV state authoritative without
// blocking the visual response on the round trip.
//
// The next sort state comes from the button's `data-next-sort-state`
// attribute, which the server pre-computed via `Table.toggle_sort/2`.
// The hook never reimplements that toggle rule — guarantees the
// optimistic mirror agrees with what the server will emit.
const handleSortClick = (
  tbody: HTMLElement,
  table: HTMLTableElement,
  e: Event,
): void => {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const btn = target.closest<HTMLElement>("button[data-sort-key]");
  if (btn === null || !table.contains(btn)) return;
  const next = parseSortStateAttr(btn.getAttribute("data-next-sort-state"));
  if (next === null) return;

  const prev = snapshotRowRects(tbody);
  sortRowsInDom(tbody, next);
  applyHeaderState(tbody, table, next);
  playFlip(tbody, prev);
};

export const TableSortAnimate = {
  mounted(this: LiveViewHook<never> & TableSortAnimateState) {
    this._prevRects = undefined;
    const table = this.el.closest<HTMLTableElement>("table");
    this._table = table;
    if (table !== null) {
      const handler = (e: Event) => {
        handleSortClick(this.el, table, e);
      };
      this._clickHandler = handler;
      table.addEventListener("click", handler);
    } else {
      this._clickHandler = null;
    }
  },
  beforeUpdate(this: LiveViewHook<never> & TableSortAnimateState) {
    this._prevRects = snapshotRowRects(this.el);
  },
  updated(this: LiveViewHook<never> & TableSortAnimateState) {
    const prev = this._prevRects;
    this._prevRects = undefined;
    if (prev === undefined) return;
    playFlip(this.el, prev);
  },
  destroyed(this: LiveViewHook<never> & TableSortAnimateState) {
    if (this._table !== null && this._clickHandler !== null) {
      this._table.removeEventListener("click", this._clickHandler);
    }
    this._table = null;
    this._clickHandler = null;
    this._prevRects = undefined;
  },
};

// Test-only exports. Production builds tree-shake these (vite/rollup).
export const __testing = {
  snapshotRowRects,
  playFlip,
  parseSortStateAttr,
  compareValues,
  rowSortValue,
  sortRowsInDom,
  applyHeaderState,
  handleSortClick,
};
